import React, { useEffect, useRef, useState } from 'react';
import { Error, WarningTriangle } from '@withjoy/joykit/icons';
import { Flex, useToast } from '@withjoy/joykit';
import SortableList from '@shared/components/SortableList';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { CheckIcon } from '@assets/icons';
import { useUpdateTravelMapComponentMutation } from '@graphql/generated';
import { sortByOrderList } from '@shared/utils/array';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { AccommodationCard, TravelMapBlockType } from '../../Accommodations.types';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { SortableCard } from '../SortableCard/SortableCard';
import { addError } from '@shared/utils/logger';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { pxToRem } from '@withjoy/joykit/theme';
import { useThrottledWindowResize } from '@shared/utils/hooks/useThrottledWindowResize';
import { useAutoPopulatedAccommodations } from '../../hooks/useAutoPopulatedAccommodations';

interface SortableCardListProps {
  accommodationHotels: AccommodationCard[];
  blockOrdering?: string[];
  showAutoPopulateInsights?: boolean;
}

export const SortableCardList = ({ accommodationHotels, blockOrdering, showAutoPopulateInsights = false }: SortableCardListProps) => {
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { toast } = useToast();
  const [updateTravelMapComponent] = useUpdateTravelMapComponentMutation();
  const { setCustomComponentBlockData, setSelectedHotelPhotos } = useCustomComponentStateContext();
  const { navigateToScreen } = useNavigationPaneContext();
  const telemetry = useAdminAccommodationsTelemetry();

  const { t } = useTranslation('websiteEditor');
  const transCustomPage = t('customPage');
  const transHotel = t('customPage', 'travelMap', 'hotel');

  const { componentData, accommodationsData, isSuperAdmin, accommodationPropertyPhotos } = useAccommodationsContext();
  const { isFirstTimeAutoPopulation } = useAutoPopulatedAccommodations();

  const [accommodationsList, setAccommodationsList] = useState<AccommodationCard[]>(accommodationHotels);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAccommodationsList(accommodationHotels);
  }, [accommodationHotels]);

  // Measure container width for consistent drag overlay sizing.
  // This fixes a width inconsistency issue where cards shrink during drag-and-drop:
  // - SortableList uses createPortal to render the drag overlay to document.body
  // - adjustOverlayScale={false} prevents automatic dimension measurement (needed to avoid visual issues)
  // - CardContainer has width="100%" which works in the list but loses context when portaled
  // - By measuring and passing explicit pixel width, both the in-list card and overlay maintain identical dimensions
  const measureWidth = useEventCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  });

  useThrottledWindowResize(measureWidth);

  const handleOnPageUpdate = useEventCallback((message?: string) => toast(`${message}`, { icon: <CheckIcon /> }));
  const handleOnError = (message: string) => toast(`${message}`, { icon: <WarningTriangle /> });

  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragStart = useEventCallback(() => {
    setIsDragActive(true);
  });

  const handleDragCancel = useEventCallback(() => {
    setIsDragActive(false);
  });

  const handleDragEnd = useEventCallback((dragData: { fromIndex: number; toIndex: number; items: AccommodationCard[]; reorderedItem: AccommodationCard | undefined }) => {
    setIsDragActive(false);

    const hasSortOrderChanged = !dragData.items.every((item, index) => item.id === accommodationsList[index].id);

    if (!hasSortOrderChanged) return;
    // If the sort order has changed, update the sort order in the backend
    const isFavorite = accommodationsList.find(item => item.isFavorite);
    // Check if favorite hotel order changed then don't save order
    if (isFavorite && !dragData.items[0].isFavorite) {
      toast(`${transHotel.changeOrderInfo()}`, { icon: <Error size={24} /> });
      const accOrder = sortByOrderList(accommodationsList ?? [], blockOrdering);
      setAccommodationsList(accOrder);
      return;
    }
    setAccommodationsList(dragData.items);

    updateTravelMapComponent({
      variables: {
        payload: {
          id: componentData?.id,
          sortOrder: componentData?.sortOrder ?? 1,
          blockOrdering: dragData.items.map(item => item.id),
        },
      },
      refetchQueries: ['PageContainerBySlug'],
    })
      .then(response => {
        if (response.errors) {
          addError(response.errors[0], { context: 'Accommodations failed to update sort order' });
          handleOnError(transCustomPage.changeBlockOrderFailed());
        }
        if (response.data) {
          updateWebsitePreview();
          handleOnPageUpdate(transCustomPage.changeBlockOrderSuccess());
        }
      })
      .catch(error => {
        addError(error, { context: 'Accommodations failed to update sort order' });
        handleOnError(transCustomPage.changeBlockOrderFailed());
      });
  });

  const handleHotelClick = useEventCallback((hotel: AccommodationCard) => {
    const accommodationPhotos = accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(place => place.joyPlaceId === hotel.joyPlaceId);
    switch (hotel.type) {
      case TravelMapBlockType.travelMapAccommodationsRoomblock: {
        const roomBlockToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!roomBlockToEdit) {
          addError('Unable to find room block error');
          return;
        }
        telemetry.editItemClicked('accommodations.RoomBlock', { itemType: hotel.type, isSuperAdmin: !!isSuperAdmin });

        setSelectedHotelPhotos(accommodationPhotos?.photos ?? []);
        setCustomComponentBlockData(roomBlockToEdit);
        if (roomBlockToEdit.isCustomLinkEnabled || !roomBlockToEdit.zhId) {
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_EDIT);
        } else {
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_EDIT);
        }
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsHotel: {
        const hotelToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!hotelToEdit) {
          addError('Unable to find hotel error');
          return;
        }

        setSelectedHotelPhotos(accommodationPhotos?.photos ?? []);
        setCustomComponentBlockData(hotelToEdit);
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_EDIT);
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsCustom: {
        const customPropToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!customPropToEdit) {
          addError('Unable to find custom property error');
          return;
        }

        setCustomComponentBlockData(customPropToEdit);
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_EDIT);
        break;
      }
    }
  });

  return (
    <Flex
      ref={containerRef}
      flexDirection="column"
      gap={6}
      marginX={6}
      marginTop={5}
      paddingBottom={pxToRem(80)}
    >
      <SortableList
        items={accommodationsList}
        renderItem={(hotel, index, isItemDragActive) => {
          return (
            <SortableCard
              key={`sortable-list-card-${hotel.id}-${index}`}
              hotelIndex={index}
              onHotelClick={handleHotelClick}
              hotel={hotel}
              isDragActive={isDragActive}
              showAutoPopulateInsights={showAutoPopulateInsights}
              containerWidth={containerWidth}
              isItemDragActive={isItemDragActive}
              isFirstTimeAutoPopulation={isFirstTimeAutoPopulation}
            />
          );
        }}
        onChange={() => {}}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
        adjustOverlayScale={false}
      />
    </Flex>
  );
};
